import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

// Shown to Google users so they can also sign in with email + password
// on the same account (same farm, either method).
export const SetPasswordDialog = () => {
  const { setPassword } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPasswordValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await setPassword(password);
      toast.success('Password added. You can now sign in with your email too.');
      setPasswordValue('');
      setOpen(false);
    } catch (error) {
      toast.error(formatApiError(error, 'Could not save password'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="add-password-btn"
          className="w-full h-10 border-[#D1D1CD] text-[#2E4632] hover:bg-[#E8E8E3]"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Add a password
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#1C1C1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Add a password
          </DialogTitle>
          <DialogDescription className="text-[#5C5C5C]">
            Set a password for this account so you can sign in with your email as well as Google.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              data-testid="new-password-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              placeholder="At least 8 characters"
              className="h-12 border-[#D1D1CD]"
            />
            <p className="text-xs text-[#5C5C5C]">
              Use 8+ characters with at least one letter and one number.
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1 h-12 border-[#D1D1CD]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="save-password-btn"
              disabled={submitting}
              className="flex-1 h-12 bg-[#2E4632] hover:bg-[#1F3122] text-white"
            >
              {submitting ? 'Saving...' : 'Save password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
